import classNames from 'classnames';
import React, { useState } from 'react';
import { HiThumbUp, HiThumbDown } from 'react-icons/hi';
import { AiFillMessage } from 'react-icons/ai';
import { IoCopy } from 'react-icons/io5';
import ShareIcon from '../../../assets/icons/share.png';
import { IPostModel } from '../../../models';
import PostDonate from '../../post-donate/post-donate.component';
import _ from 'lodash';
import ModalSharePost from '../../../pages/home/components/modal.share.post';
import { useReactPost } from '../../../hooks/use-react-post/useReactPost';
import toast from 'react-hot-toast';
import ConfirmReactPost from '../../cofirm-react-post/cofirm-react-post.component';
import { useNavigate } from 'react-router-dom';
import useBookmark from '../../../hooks/use-bookmark/useBookMark';
import { useSelector } from 'react-redux';
import { RootState } from '../../../configuration';
import { PostListAction } from './post-list-action';
import { Tooltip } from 'antd';
import { Eye, ThumbsUp, ThumbsDown, MessageCircle, Users } from 'lucide-react';
import { formatViews } from '../../../utils/view-count';

interface PostActionsComponentProps {
  post: IPostModel;
  isPostUser: boolean;
  profileUser?: boolean;
  customClassName?: string;
  /** Bấm nút bình luận → cuộn tới + focus ô nhập bình luận của bài. */
  onCommentClick?: () => void;
}

type ReactPostState = {
  visible: boolean;
  react: 'like' | 'dislike' | 'idle';
};

const PostActionsComponent = ({
  post,
  isPostUser,
  profileUser,
  customClassName,
  onCommentClick,
}: PostActionsComponentProps) => {
  const navigate = useNavigate();
  const [visibleShare, setVisibleShare] = useState(false);
  const {
    liked,
    disliked,
    handleLikePost,
    handleDisLikePost,
    handleShowConfirm,
  } = useReactPost({
    postId: _.get(post, 'id', ''),
  });
  const [reactPostState, setReactPostState] = useState<ReactPostState>({
    visible: false,
    react: 'idle',
  });
  const [showListAction, setShowListAction] = useState<boolean>(false);
  const profile = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const showModalSharePreview = () => {
    if (!profileUser) {
      navigate('/account/login');
      return;
    }
    setVisibleShare(true);
  };

  const onLikePost = () => {
    if (!profileUser) {
      navigate('/account/login');
      return;
    }
    if (
      post?.currentUserHasVoteUp ||
      post?.currentUserHasVoteDown ||
      liked ||
      disliked
    ) {
      toast.success('Bạn đã tương tác với bài viết này!', {
        position: 'top-right',
      });
      return;
    }
    if (profile && profile.shouldShowPostAlert) {
      setReactPostState({
        visible: true,
        react: 'like',
      });
    } else {
      handleLikePost();
    }
  };

  const onDisLikePost = () => {
    if (!profileUser) {
      navigate('/account/login');
      return;
    }
    if (
      post?.currentUserHasVoteUp ||
      post?.currentUserHasVoteDown ||
      liked ||
      disliked
    ) {
      toast.success('Bạn đã tương tác với bài viết này!', {
        position: 'top-right',
      });
      return;
    }

    if (profile && profile.shouldShowPostAlert) {
      setReactPostState({
        visible: true,
        react: 'dislike',
      });
    } else {
      handleDisLikePost();
    }
  };

  const onConfirmReact = async (suppressDays: number | null) => {
    if (!profileUser) {
      navigate('/account/login');
      return;
    }
    const { react } = reactPostState;
    switch (react) {
      case 'idle':
        break;
      case 'dislike':
        await handleDisLikePost();
        break;
      case 'like':
        await handleLikePost();
        break;

      default:
        break;
    }
    if (suppressDays !== null) {
      await handleShowConfirm((suppressDays === -1 && null) || suppressDays);
    }
    setReactPostState({
      visible: false,
      react: 'idle',
    });
  };
  const { toggleBookmark } = useBookmark();
  return (
    <>
      {reactPostState.visible ? (
        <ConfirmReactPost
          isLike={reactPostState.react === 'like'}
          visible={reactPostState.visible}
          onClose={() =>
            setReactPostState({
              visible: false,
              react: 'idle',
            })
          }
          onCancel={() =>
            setReactPostState({
              visible: false,
              react: 'idle',
            })
          }
          onConfirm={onConfirmReact}
        />
      ) : null}

      <div
        className={`sm:p-4 p-2.5 flex items-center flex-wrap justify-between gap-4 text-xs font-semibold ${customClassName}`}
      >
        <div className="flex gap-2 items-center">
          <Tooltip title={post?.currentUserHasVoteUp || liked ? 'Đã thích' : 'Thích bài viết'}>
            <div
              onClick={() => {
                onLikePost();
              }}
              className={classNames(
                'flex items-center cursor-pointer px-[10px] gap-1.5 min-w-[52px] justify-center h-8 rounded-full text-sm transition-colors',
                post?.currentUserHasVoteUp || liked
                  ? 'bg-blue-500 text-white'
                  : 'bg-[#EAEFFA] dark:bg-slate-700 text-[#657384] dark:text-slate-50 hover:bg-blue-100 dark:hover:bg-slate-600'
              )}
            >
              <ThumbsUp
                size={15}
                className={post?.currentUserHasVoteUp || liked ? 'fill-current' : ''}
              />
              <span>{_.get(post, 'voteUpCount', 0) + (liked ? 1 : 0)}</span>
            </div>
          </Tooltip>

          <Tooltip title={post?.currentUserHasVoteDown || disliked ? 'Đã không thích' : 'Không thích'}>
            <div
              onClick={() => {
                onDisLikePost();
              }}
              className={classNames(
                'flex items-center cursor-pointer px-[10px] gap-1.5 min-w-[52px] justify-center h-8 rounded-full text-sm transition-colors',
                post?.currentUserHasVoteDown || disliked
                  ? 'bg-red-500 text-white'
                  : 'bg-[#EAEFFA] dark:bg-slate-700 text-[#657384] dark:text-slate-50 hover:bg-red-100 dark:hover:bg-slate-600'
              )}
            >
              <ThumbsDown
                size={15}
                className={post?.currentUserHasVoteDown || disliked ? 'fill-current' : ''}
              />
              <span>{_.get(post, 'voteDownCount', 0) + (disliked ? 1 : 0)}</span>
            </div>
          </Tooltip>

          <Tooltip title="Bình luận">
            <div
              onClick={onCommentClick}
              className={classNames(
                'flex items-center px-[10px] gap-1.5 min-w-[52px] justify-center h-8 rounded-full text-sm bg-[#EAEFFA] dark:bg-slate-700 text-[#657384] dark:text-slate-50 transition-colors',
                onCommentClick ? 'cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600' : ''
              )}
            >
              <MessageCircle size={15} />
              <span>{post?.commentsCount}</span>
            </div>
          </Tooltip>

          <Tooltip title="Lượt xem">
            <div className="flex items-center px-[10px] gap-1.5 h-8 rounded-full bg-[#EAEFFA] dark:bg-slate-700 text-sm text-[#657384] dark:text-slate-50">
              <Eye size={16} />
              <span>{formatViews(post)}</span>
            </div>
          </Tooltip>
          <Tooltip title="Danh sách người tương tác">
            <div
              onClick={() => setShowListAction(true)}
              className="flex items-center px-[10px] gap-1.5 cursor-pointer h-8 rounded-full bg-[#EAEFFA] dark:bg-slate-700 text-sm text-[#657384] dark:text-slate-50 transition-colors hover:bg-slate-200 dark:hover:bg-slate-600"
            >
              <Users size={16} />
            </div>
          </Tooltip>
          <PostListAction
            isMe={profile?.userId === post?.userId}
            id={post?.id}
            visibleUpload={showListAction}
            onClose={() => {
              setShowListAction(false);
            }}
          />
        </div>

        <div className="flex items-center gap-2 ">
          <Tooltip title="Lưu bài viết">
            <button
              onClick={() => {
                post?.id && toggleBookmark(post?.id);
                toast.success('Đã lưu bài viết');
              }}
              type="button"
              className={isPostUser ? 'button-icon ml-auto' : 'button-icon'}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="32" height="32" rx="16" className="fill-[#EAEFFA] dark:fill-slate-700" />
                <path
                  d="M16.594 11.92H11.4073C10.2673 11.92 9.33398 12.8534 9.33398 13.9934V21.5667C9.33398 22.5334 10.0273 22.9467 10.874 22.4734L13.494 21.0134C13.774 20.86 14.2273 20.86 14.5007 21.0134L17.1207 22.4734C17.974 22.94 18.6673 22.5334 18.6673 21.5667V13.9934C18.6673 12.8534 17.734 11.92 16.594 11.92Z"
                  fill="#657384"
                />
                <path
                  d="M22.6659 11.4067V18.98C22.6659 19.9467 21.9726 20.3534 21.1259 19.8867L19.8392 19.1667C19.7326 19.1067 19.6659 18.9934 19.6659 18.8734V13.9934C19.6659 12.3 18.2859 10.92 16.5926 10.92H13.8792C13.6326 10.92 13.4592 10.66 13.5726 10.4467C13.9192 9.78671 14.6126 9.33337 15.4059 9.33337H20.5926C21.7326 9.33337 22.6659 10.2667 22.6659 11.4067Z"
                  fill="#657384"
                />
              </svg>
            </button>
          </Tooltip>
          <Tooltip title="Chia sẻ">
            <button
              type="button"
              className={isPostUser ? 'button-icon ml-auto' : 'button-icon'}
              onClick={showModalSharePreview}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="32" height="32" rx="16" className="fill-[#EAEFFA] dark:fill-slate-700" />
                <path
                  d="M22.3562 14.8562L19.3562 17.8562C19.2603 17.948 19.1328 17.9994 19 18C18.9336 17.9997 18.8679 17.987 18.8062 17.9625C18.7152 17.9242 18.6376 17.8599 18.5831 17.7776C18.5286 17.6953 18.4997 17.5987 18.5 17.5V15C17.2805 14.999 16.0953 15.4038 15.1311 16.1506C14.167 16.8975 13.4788 17.9439 13.175 19.125C13.1462 19.2323 13.0828 19.3272 12.9947 19.395C12.9066 19.4628 12.7987 19.4997 12.6875 19.5L12.5625 19.4812C12.4353 19.4474 12.3265 19.3648 12.2598 19.2513C12.1931 19.1378 12.1739 19.0026 12.2063 18.875C12.5655 17.4792 13.379 16.2425 14.5185 15.3599C15.6579 14.4773 17.0587 13.9989 18.5 14V11.5C18.4997 11.4013 18.5286 11.3047 18.5831 11.2224C18.6376 11.1401 18.7152 11.0757 18.8062 11.0375C18.8986 11.0015 18.9993 10.9923 19.0966 11.0111C19.1939 11.0299 19.284 11.0759 19.3562 11.1437L22.3562 14.1437C22.4501 14.2385 22.5028 14.3666 22.5028 14.5C22.5028 14.6334 22.4501 14.7614 22.3562 14.8562ZM20 21H10.5V13.5C10.5 13.3674 10.4473 13.2402 10.3536 13.1464C10.2598 13.0527 10.1326 13 10 13C9.86739 13 9.74021 13.0527 9.64645 13.1464C9.55268 13.2402 9.5 13.3674 9.5 13.5V21C9.5 21.2652 9.60536 21.5195 9.79289 21.7071C9.98043 21.8946 10.2348 22 10.5 22H20C20.1326 22 20.2598 21.9473 20.3536 21.8535C20.4473 21.7598 20.5 21.6326 20.5 21.5C20.5 21.3674 20.4473 21.2402 20.3536 21.1464C20.2598 21.0527 20.1326 21 20 21Z"
                  fill="#768293"
                />
              </svg>
            </button>
          </Tooltip>
          {!isPostUser && profileUser && (
            <div className="ml-auto">
              <PostDonate post={post} />
            </div>
          )}
        </div>
      </div>
      {visibleShare && (
        <ModalSharePost
          onFetch={() => {
            //
          }}
          isOpen={visibleShare}
          reset={() => setVisibleShare(false)}
          post={post}
        />
      )}
    </>
  );
};

export default PostActionsComponent;
